import { formatPaise, lineTax, readRupees, taxableFromInclusive } from '@oneapp/gst';
import type { WalkIn } from '@oneapp/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '../components/icons';
import { Sheet } from '../components/Sheet';
import { Button, Callout, Chips } from '../components/ui';
import { ApiError } from '../lib/api';
import { salesKeys } from '../sales/api';
import { parseWalkInPath, recordsApi, recordsKeys, type WalkInStart } from './api';

/**
 * R11 · Walk-in sales: what walk-in customers paid in the month at each rate, including GST (each rate
 * is one total in Table 7), with the sales before GST and the tax worked out as it's typed; then the
 * first and last bill numbers and the cancelled ones, counted against the bills already in the return.
 * These are the owner's own figures: nothing here is estimated. Opened from the chat ("Different rates",
 * "Change"), from the month's sales, or on its own from a link.
 */

export type WalkInFinish = 'saved' | 'back';

export interface WalkInScreen {
  label: string;
  body: ReactNode;
  footer: ReactNode;
}

interface Row {
  key: number;
  rate: number;
  /** As typed, in rupees. */
  paid: string;
}

/** The rates a walk-in total can be at, for the month (GST 2.0 from 22 September 2025). */
const ratesFor = (period: string) => (period >= '2025-10' ? [5, 18, 40, 3, 1.5, 0.25] : [5, 12, 18, 28, 3, 0.25]);

const rupees = (paise: number) =>
  (paise / 100).toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: paise % 100 ? 2 : 0 });

let keys = 0;

export function useWalkInScreen(
  returnId: string,
  start: WalkInStart,
  finish: (how: WalkInFinish) => void,
): WalkInScreen {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = useId();
  const query = useQuery({
    queryKey: recordsKeys.walkIn(returnId),
    queryFn: ({ signal }) => recordsApi.walkIn(returnId, signal),
    staleTime: 0,
  });
  const [rows, setRows] = useState<Row[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [cancelled, setCancelled] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  /** "Add a rate" shows the rates not used yet, to pick one. */
  const [choosing, setChoosing] = useState(false);

  // The form starts from what's saved, else from what the chat worked out.
  const saved = query.data?.walkIn ?? null;
  useEffect(() => {
    if (!query.data || rows) return;
    const from: WalkIn | null = query.data.walkIn;
    if (from) {
      setRows(from.rates.map((r) => ({ key: ++keys, rate: r.rate, paid: rupees(r.paid) })));
      setFrom(from.bills[0]?.from ?? '');
      setTo(from.bills[0]?.to ?? '');
      setCancelled(from.cancelled.join(', '));
    } else {
      setRows([{ key: ++keys, rate: start.rate ?? 18, paid: start.paid ? rupees(start.paid) : '' }]);
      setFrom(start.from);
      setTo(start.to);
    }
  }, [query.data, rows, start.rate, start.paid, start.from, start.to]);

  const draft = query.data?.return;
  const period = draft?.period ?? '';
  const month = period ? t(`filings.months.${Number(period.slice(5, 7))}`) : '';
  const rates = ratesFor(period);
  const cancelledList = cancelled
    .split(/[\s,;]+/)
    .map((n) => n.trim())
    .filter(Boolean);

  // The bills from first to last, counted against the other sales in the return.
  const range = from.trim() && to.trim() ? { from: from.trim(), to: to.trim() } : null;
  const count = useQuery({
    queryKey: recordsKeys.bills(returnId, range?.from ?? '', range?.to ?? ''),
    queryFn: ({ signal }) => recordsApi.countBills(returnId, [range as { from: string; to: string }], signal),
    enabled: range !== null,
    retry: false,
  });

  const screen: WalkInScreen = {
    label: month ? t('records.walkIn.label', { month }) : t('sales.loading'),
    body: null,
    footer: null,
  };
  if (query.isPending || (query.data && !rows)) {
    screen.body = <p className="muted">{t('sales.loading')}</p>;
    return screen;
  }
  if (!query.data || !rows) {
    const notFound = query.error instanceof ApiError && query.error.status === 404;
    screen.body = (
      <Callout tone="error" icon="alert-triangle">
        {notFound ? t('records.walkIn.notFound') : t('sales.loadFailed')}
      </Callout>
    );
    return screen;
  }

  const worked = rows.map((row) => {
    const paid = readRupees(row.paid);
    if (paid === null) return { row, paid: null, taxable: 0, tax: 0 };
    const taxable = taxableFromInclusive(paid, row.rate);
    const tax = lineTax(taxable, row.rate, false);
    return { row, paid, taxable, tax: tax.cgst + tax.sgst };
  });
  const totals = worked.reduce(
    (sum, w) => ({ paid: sum.paid + (w.paid ?? 0), taxable: sum.taxable + w.taxable, tax: sum.tax + w.tax }),
    { paid: 0, taxable: 0, tax: 0 },
  );
  const update = (key: number, change: Partial<Row>) =>
    setRows((before) => (before ?? []).map((row) => (row.key === key ? { ...row, ...change } : row)));
  const unused = rates.filter((rate) => !rows.some((row) => row.rate === rate));

  const save = async () => {
    const found: Record<string, string> = {};
    worked.forEach((w, i) => {
      if (w.paid === null || w.paid <= 0) found[`rates.${i}.paid`] = t('records.walkIn.needAmount');
    });
    if (!range) found.bills = t('records.walkIn.needBills');
    setErrors(found);
    setProblem(null);
    if (Object.keys(found).length || !range) return;
    setBusy(true);
    try {
      const result = await recordsApi.saveWalkIn(returnId, {
        rates: worked.map((w) => ({ rate: w.row.rate, paid: w.paid as number })),
        bills: [range],
        cancelled: cancelledList,
      });
      queryClient.setQueryData(recordsKeys.walkIn(returnId), result);
      queryClient.setQueryData(salesKeys.draft(returnId), { return: result.return });
      void queryClient.invalidateQueries({ queryKey: salesKeys.all(returnId) });
      finish('saved');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setErrors(error.problem?.fields ?? {});
      setProblem(error.problem?.detail ?? t('records.walkIn.failed'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    setBusy(true);
    try {
      const result = await recordsApi.removeWalkIn(returnId);
      queryClient.setQueryData(recordsKeys.walkIn(returnId), result);
      queryClient.setQueryData(salesKeys.draft(returnId), { return: result.return });
      void queryClient.invalidateQueries({ queryKey: salesKeys.all(returnId) });
      finish('back');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      setProblem(t('records.walkIn.failed'));
    } finally {
      setBusy(false);
    }
  };

  const counted = count.data;
  const walkInBills = counted ? counted.all - counted.others - cancelledList.length : 0;
  const rangeError =
    errors['bills.0'] ?? errors.bills ?? (count.error instanceof ApiError ? count.error.problem?.detail : undefined);

  screen.body = (
    <form
      id={formId}
      className="walkin"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <p className="muted">{t('records.walkIn.lede', { month })}</p>
      {worked.map((w, i) => {
        const paidId = `${formId}-paid-${w.row.key}`;
        const helpId = `${paidId}-help`;
        const error = errors[`rates.${i}.paid`] ?? errors[`rates.${i}.rate`];
        return (
          <div key={w.row.key} className="field walkin__rate">
            <label className="field__label" htmlFor={paidId}>
              {t('records.walkIn.at', { rate: w.row.rate })}
            </label>
            <div className="walkin__inputs">
              <div className="money walkin__money">
                <input
                  id={paidId}
                  className="input"
                  inputMode="decimal"
                  autoComplete="off"
                  value={w.row.paid}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={helpId}
                  onChange={(event) => update(w.row.key, { paid: event.target.value })}
                />
              </div>
              {rows.length > 1 ? (
                <button
                  type="button"
                  className="iconbtn"
                  aria-label={t('records.walkIn.removeRate', { rate: w.row.rate })}
                  onClick={() => setRows((before) => (before ?? []).filter((row) => row.key !== w.row.key))}
                >
                  <Icon name="x" size={18} />
                </button>
              ) : null}
            </div>
            <p id={helpId} className={`field__help${error ? ' field__help--error' : ''}`} aria-live="polite">
              {error ??
                (w.paid !== null
                  ? t('records.walkIn.paidHelp', { taxable: formatPaise(w.taxable), tax: formatPaise(w.tax) })
                  : '')}
            </p>
          </div>
        );
      })}
      {unused.length ? (
        choosing ? (
          <Chips
            label={t('records.walkIn.addRate')}
            chips={unused.map((rate) => ({ id: String(rate), label: `${rate}%` }))}
            onPick={(chip) => {
              setRows((before) => [...(before ?? []), { key: ++keys, rate: Number(chip.id), paid: '' }]);
              setChoosing(false);
            }}
          />
        ) : (
          <div>
            <Button variant="secondary" small inline icon="plus" onClick={() => setChoosing(true)}>
              {t('records.walkIn.addRate')}
            </Button>
          </div>
        )
      ) : null}
      <dl className="fields walkin__totals">
        <div className="fields__row">
          <dt>{t('records.walkIn.paid')}</dt>
          <dd>{formatPaise(totals.paid)}</dd>
        </div>
        <div className="fields__row fields__row--emphasis">
          <dt>{t('records.walkIn.taxable')}</dt>
          <dd>{formatPaise(totals.taxable)}</dd>
        </div>
        <div className="fields__row">
          <dt>
            {t('records.walkIn.tax')}
            <span className="fields__hint">{t('records.walkIn.taxHelp')}</span>
          </dt>
          <dd>{formatPaise(totals.tax)}</dd>
        </div>
      </dl>

      <h3 className="walkin__section">{t('records.walkIn.bills')}</h3>
      <div className="walkin__pair">
        <BillField label={t('records.walkIn.from')} value={from} onChange={setFrom} invalid={Boolean(rangeError)} />
        <BillField label={t('records.walkIn.to')} value={to} onChange={setTo} invalid={Boolean(rangeError)} />
      </div>
      {rangeError ? (
        <p className="field__help field__help--error" role="alert">
          {rangeError}
        </p>
      ) : null}
      <BillField
        label={t('records.walkIn.cancelled')}
        value={cancelled}
        onChange={setCancelled}
        help={errors.cancelled ?? t('records.walkIn.cancelledHelp')}
        invalid={Boolean(errors.cancelled)}
        maxLength={400}
      />
      {counted && walkInBills >= 0 ? (
        <Callout icon="file-check" role="status">
          {counted.others
            ? t(counted.others === 1 ? 'records.walkIn.noteOneOther' : 'records.walkIn.note', {
                all: counted.all,
                others: counted.others,
                cancelled: cancelledList.length,
                walkIn: walkInBills,
              })
            : t('records.walkIn.noteNoOthers', {
                all: counted.all,
                cancelled: cancelledList.length,
                walkIn: walkInBills,
              })}
        </Callout>
      ) : null}
      <p className="muted walkin__hsn">{t('records.walkIn.hsnNote')}</p>
      {problem ? (
        <Callout tone="error" icon="alert-triangle" role="alert">
          {problem}
        </Callout>
      ) : null}
      {saved ? (
        <Button variant="ghost" small inline icon="trash" disabled={busy} onClick={remove}>
          {t('records.walkIn.remove')}
        </Button>
      ) : null}
    </form>
  );
  screen.footer = (
    <Button type="submit" form={formId} busy={busy}>
      {t('records.walkIn.save')}
    </Button>
  );
  return screen;
}

function BillField({
  label,
  value,
  onChange,
  help,
  invalid,
  maxLength = 40,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  help?: string;
  invalid?: boolean;
  maxLength?: number;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input input--mono"
        value={value}
        autoComplete="off"
        autoCapitalize="characters"
        spellCheck={false}
        maxLength={maxLength}
        aria-invalid={invalid ? true : undefined}
        aria-describedby={help ? `${id}-help` : undefined}
        onChange={(event) => onChange(event.target.value.toUpperCase())}
      />
      {help ? (
        <p id={`${id}-help`} className={`field__help${invalid ? ' field__help--error' : ''}`}>
          {help}
        </p>
      ) : null}
    </div>
  );
}

/** Inside the month's sales screens (a sheet or a page of their own): the form with its own button. */
export function WalkInEmbed({ returnId, onDone }: { returnId: string; onDone: () => void }) {
  const screen = useWalkInScreen(returnId, { paid: null, rate: null, from: '', to: '' }, onDone);
  return (
    <div className="walkin__embed">
      {screen.body}
      {screen.footer}
    </div>
  );
}

/** Opened from the chat: the sheet over it. */
export function WalkInSheet({
  path,
  onClose,
  onFinish,
}: {
  path: string;
  onClose: () => void;
  onFinish: (how: WalkInFinish) => void;
}) {
  const parsed = parseWalkInPath(path);
  const screen = useWalkInScreen(
    parsed?.returnId ?? '',
    parsed ?? { paid: null, rate: null, from: '', to: '' },
    onFinish,
  );
  return (
    <Sheet open onClose={onClose} label={screen.label} footer={screen.footer}>
      {screen.body}
    </Sheet>
  );
}
